import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeQuantityInput, selectQuantity } from '../../helpers/taxPlanner/fifoSelection';
import { D, formatWholeUah, roundFinal } from '../../helpers/taxPlanner/money';
import { pluralForm } from '../../helpers/pluralForm';
import Badge from '../Badge';
import Checkbox from '../Checkbox';
import ChevronRight from '../icons/ChevronRight';
import Switch from '../Switch';
import TextField from '../TextField';
import { Tooltip } from '../Tooltip';
import { jurisdiction } from '../../config/taxPlannerJurisdiction';
import { positionKey } from '../../helpers/taxPlanner/totals';

const MINUS = '−';
// Mock `tr.issuer.sel`: the accent at 6 % over the surface.
const SELECTED_ROW = 'bg-[color-mix(in_srgb,rgb(var(--accent-solid))_6%,rgb(var(--surface)))]';
const SYMBOLS = { USD: '$', EUR: '€', GBP: '£' };

export const currencySymbol = (code) => SYMBOLS[code] ?? code;

const withSpaces = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

// Currency amount: two decimals with a decimal comma, symbol after; `signed` adds + / −.
export const formatMoney = (value, currency, { signed = false } = {}) => {
  const d = roundFinal(value);
  const [whole, frac] = d.abs().toFixed(2).split('.');
  const sign = d.isZero() ? '' : d.isNegative() ? MINUS : signed ? '+' : '';
  return `${sign}${withSpaces(whole)},${frac} ${currencySymbol(currency)}`;
};

// Whole hryvnias with sign: «+380 ₴», «−1 440 ₴», «0 ₴».
export const formatUah = (value, mode) => {
  const rounded = roundFinal(value, { scale: 0, mode: mode ?? 'HALF_UP' });
  if (rounded.isZero()) return `0 ${jurisdiction.currencySymbol}`;
  return `${rounded.isNegative() ? MINUS : '+'}${formatWholeUah(rounded.abs(), mode)} ${jurisdiction.currencySymbol}`;
};

// A quantity as money is shown: thousands spaced, a decimal comma («1 500», «2,5», «−10»).
export const formatQuantity = (value) => {
  const d = D(value);
  const [whole, frac] = d.abs().toFixed().split('.');
  return `${d.isNegative() ? MINUS : ''}${withSpaces(whole)}${frac ? `,${frac}` : ''}`;
};
// The quantity in the field: a decimal comma, no spacing, empty for nothing selected (placeholder «0»).
const fieldQuantity = (value) => (D(value).isZero() ? '' : D(value).abs().toFixed().replace('.', ','));
export const formatRate = (rate) => `${roundFinal(rate).toFixed(2).replace('.', ',')}`;
export const isoToDotted = (iso) => iso.slice(0, 10).split('-').reverse().join('.');


export const sign = (value) => {
  const d = D(value);
  return d.isZero() ? 0 : d.isNegative() ? -1 : 1;
};

// AC-16: the currency result and the UAH result have different (non-zero) signs.
export const signFlipped = (result, resultUah) =>
  result != null && resultUah != null && sign(result) * sign(resultUah) < 0;

export const checkboxState = (state) => (state === 'all' ? 'checked' : state === 'partial' ? 'indeterminate' : 'unchecked');

// Impact on the tax: 0 is muted, otherwise signed (a decrease is good).
export function ImpactValue({ value, rounding }) {
  const zero = D(value).isZero();
  return <span className={clsx('num', zero ? 'text-text-muted' : D(value).isNegative() ? 'text-success-ink' : 'text-danger')}>{formatUah(value, rounding)}</span>;
}

const resultTone = (value) => (sign(value) < 0 ? 'text-danger' : sign(value) > 0 ? 'text-success-ink' : '');

export function ResultValue({ value, formatted }) {
  return <span className={clsx('num', resultTone(value))}>{formatted}</span>;
}

const Dash = () => <span className="text-text-muted">—</span>;
export { Dash };

// «До продажу» input: every input is applied at once, as in the mock (T54) — no Enter or blur needed; a decimal comma
// and spaces are accepted. While the field has focus its text is the investor's own («1,» stays «1,»); leaving it shows
// the applied quantity (a clamped one as N). A rejected value stays in the field with the allowed range.
function QuantityField({ position, value, disabled, onCommit }) {
  const { t } = useTranslation();
  const k = (key, opts) => t(`taxPlanner.scr02.positions.${key}`, opts);
  const [draft, setDraft] = useState(() => fieldQuantity(value));
  const [error, setError] = useState(null);
  const focused = useRef(false);

  useEffect(() => {
    if (focused.current) return;
    setDraft(fieldQuantity(value));
    setError(null);
  }, [value]);

  const apply = (text) => {
    setDraft(text);
    const result = selectQuantity(position, normalizeQuantityInput(text));
    if (!result.ok) {
      const max = formatQuantity(position.quantity).replace(MINUS, '');
      setError(k('rangeFraction', { max, scale: result.scale }));
      return;
    }
    setError(null);
    if (result.quantity !== value) onCommit(result.quantity);
  };

  const leave = () => {
    focused.current = false;
    if (!error) setDraft(fieldQuantity(value));
  };

  return (
    <TextField
      inputClassName="w-[76px] text-right text-[13px]"
      aria-label={k('quantityLabel', { ticker: position.ticker })}
      type="text"
      inputMode="decimal"
      placeholder="0"
      value={draft}
      disabled={disabled}
      error={error}
      onFocus={() => {
        focused.current = true;
      }}
      onChange={(e) => apply(e.target.value)}
      onBlur={leave}
    />
  );
}

/**
 * One position row. Presentation only; `selection` is the fifoSelection result for the current quantity (or null),
 * `impact` the {impact} entry of selectImpact for this position (or undefined). Handlers are optional (T28 wires them).
 */
function PositionRow({
  position,
  account,
  strategic,
  state,
  selection,
  impact,
  expanded,
  rounding,
  onToggleExpand,
  onToggleSelect,
  onToggleStrategic,
  onChangeQuantity,
}) {
  const { t, i18n } = useTranslation();
  const k = (key, opts) => t(`taxPlanner.scr02.positions.${key}`, opts);
  const key = positionKey(position);
  const testId = `position-row-${key}`;
  const noPrice = position.price === null;
  const lotCount = position.lots.length;
  const broker = account ? t(`taxPlanner.scr02.positions.brokerShort.${account.dataProvider}`, { defaultValue: account.dataProvider }) : '';
  const reason = k(`noPriceReasons.${position.noPriceReason}`, { defaultValue: k('noPriceReasons.unknown') });

  return (
    <tr
      data-testid={testId}
      className={clsx(
        'border-t border-border align-middle',
        selection ? SELECTED_ROW : 'bg-surface hover:bg-surface-muted',
      )}>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={k('showLots', { ticker: position.ticker })}
            onClick={onToggleExpand}
            className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text"
          >
            <span className={clsx('inline-flex transition-transform', expanded && 'rotate-90')}>
              <ChevronRight color="currentColor" />
            </span>
          </button>
          {noPrice ? (
            // AC-18: the checkbox is disabled; a tap on it explains why.
            <Tooltip content={reason} data-testid={`no-price-select-${key}`}>
              <Checkbox state="unchecked" disabled className="pointer-events-none" aria-label={k('selectPosition', { ticker: position.ticker })} />
            </Tooltip>
          ) : (
            <Checkbox
              state={checkboxState(state)}
              onChange={onToggleSelect}
              aria-label={k('selectPosition', { ticker: position.ticker })}
            />
          )}
        </div>
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-col gap-1">
          <b>{position.ticker}</b>
          {position.name && <small className="text-text-muted">{position.name}</small>}
          {position.side === 'SHORT' && <small className="text-text-muted">{k('noteShort')}</small>}
          {position.assetType === 'OPTION' && <small className="text-text-muted">{k('noteOption')}</small>}
          <div className="flex flex-wrap gap-1">
            {account && <Badge size="md" tone={account.stale ? 'warn' : 'accent'}>{`${broker} · ${account.originalId}`}</Badge>}
            <Badge size="md" tone="muted">{position.currency}</Badge>
            {position.assetType === 'BOND' && <Badge size="md" tone="muted">{k('badges.bond')}</Badge>}
            {position.assetType === 'OPTION' && <Badge size="md" tone="muted">{k('badges.option')}</Badge>}
            {position.side === 'SHORT' && <Badge size="md" tone="muted">{k('badges.short')}</Badge>}
            {strategic && <Badge size="md" tone="warn">{k('badges.strategic')}</Badge>}
            {signFlipped(position.unrealizedResult, position.unrealizedResultUah) && <Badge size="md" tone="muted">{k('badges.signFlip')}</Badge>}
            {noPrice && (
              <Tooltip content={reason} focusable>
                <Badge size="md" tone="warn">{k('badges.noPrice')}</Badge>
              </Tooltip>
            )}
          </div>
        </div>
      </td>
      <td data-testid={`cell-${testId}-quantity`} className="px-2 py-2 text-right num">
        {formatQuantity(position.quantity)}{' '}
        <span className="block text-text-muted text-xs">{t(`taxPlanner.scr02.positions.lots_${pluralForm(lotCount, i18n.language)}`, { count: lotCount })}</span>
      </td>
      <td data-testid={`cell-${testId}-price`} className="px-2 py-2 text-right num">
        <span className="block text-text-muted text-xs">{k('reportPrice')}</span>{' '}
        {noPrice ? k('noReportPrice') : formatMoney(position.price, position.currency)}
      </td>
      <td data-testid={`cell-${testId}-unrealized`} className="px-2 py-2 text-right">
        {noPrice ? <Dash /> : <ResultValue value={position.unrealizedResult} formatted={formatMoney(position.unrealizedResult, position.currency, { signed: true })} />}
      </td>
      <td data-testid={`cell-${testId}-unrealizedUah`} className="px-2 py-2 text-right">
        {noPrice ? <Dash /> : <ResultValue value={position.unrealizedResultUah} formatted={formatUah(position.unrealizedResultUah, rounding)} />}
      </td>
      <td data-testid={`cell-${testId}-sale`} className="px-2 py-2 text-right num text-text-muted">
        <div className="flex items-start justify-end gap-2">
          <QuantityField position={position} value={selection ? selection.quantity : '0'} disabled={noPrice} onCommit={onChangeQuantity} />
          <span className="pt-2">{k('of', { total: formatQuantity(position.quantity).replace(MINUS, '') })}</span>
        </div>
      </td>
      <td data-testid={`cell-${testId}-saleResult`} className="px-2 py-2 text-right">
        {selection ? (
          <div className="flex flex-col items-end gap-[3px]">
            <b data-testid="sale-value" className="font-semibold">
              <ResultValue value={selection.resultUah} formatted={formatUah(selection.resultUah, rounding)} />
            </b>
            {sign(selection.resultUah) > 0 && sign(position.unrealizedResultUah) < 0 ? (
              <Badge size="md" tone="danger">{k('badges.taxUp')}</Badge>
            ) : (
              state === 'partial' && <span className="text-xs text-text-muted">{k('wholePosition', { amount: formatUah(position.unrealizedResultUah, rounding) })}</span>
            )}
          </div>
        ) : (
          <Dash />
        )}
      </td>
      <td data-testid={`cell-${testId}-impact`} className="px-2 py-2 text-right">
        {selection && impact ? <ImpactValue value={impact.impact} rounding={rounding} /> : <Dash />}
      </td>
      <td className="px-2 py-2 text-center">
        <Switch
          checked={strategic}
          onChange={onToggleStrategic}
          aria-label={k('strategicSwitch', { ticker: position.ticker })}
        />
      </td>
    </tr>
  );
}

export default PositionRow;
